/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import translate from 'services/i18n/translate';
import { NavLink, useLocation } from 'react-router-dom';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { PrivateRoutePath } from 'routes/paths';

interface Props {
  active: boolean;
  searchDropdownVisible: boolean;
  dropdownVisible: boolean;
  onMenuActiveHandler: () => void;
  setDropdownVisible: (arg: boolean) => void;
}

export default function HeaderNavigation({
  active,
  searchDropdownVisible,
  dropdownVisible,
  onMenuActiveHandler,
  setDropdownVisible,
}: Props): JSX.Element {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperNavBarRef = useRef<HTMLDivElement>(null);
  const dimensions = useResizeObserver(wrapperNavBarRef);
  const [itemWith, setItemWith] = useState<number>(0);
  const [offSetMultiple, setOffSetMultiple] = useState(0);

  // Defined as a sass variable ($header-menu-item-spacing) in _vars.scss
  // Determines the space between each menu item
  const headerMenuItemSpacing = 31;

  // Menu Item 1
  const item1Ref = useRef<HTMLDivElement>(null);
  const item1Dimentions = useResizeObserver(item1Ref);
  const item1Width = Math.round(item1Dimentions.width);
  const item1Offset = 0;

  // Menu Item 2
  const item2Ref = useRef<HTMLDivElement>(null);
  const item2Dimentions = useResizeObserver(item2Ref);
  const item2Width = Math.round(item2Dimentions.width);
  // Offset is the amout of px that the bar has to navigate
  // Has to have in consideration the amount of space of itself and the previous item
  const item2Offset = item1Width + headerMenuItemSpacing;

  // Menu Item 3
  const item3Ref = useRef<HTMLDivElement>(null);
  const item3Dimentions = useResizeObserver(item3Ref);
  const item3Width = Math.round(item3Dimentions.width);
  // Offset is the amout of px that the bar has to navigate
  // Has to have in consideration the amount of space of itself and the previous item
  const item3Offset = item2Offset + item2Width + headerMenuItemSpacing;

  useEffect(() => {
    if (location.pathname.includes(PrivateRoutePath.MYTV)) {
      setOffSetMultiple(item1Offset);
      setItemWith(item1Width);
    } else if (location.pathname.includes(PrivateRoutePath.WATCHTV)) {
      setOffSetMultiple(item2Offset);
      setItemWith(item2Width);
    } else if (location.pathname.includes(PrivateRoutePath.ONDEMAND)) {
      setOffSetMultiple(item3Offset);
      setItemWith(item3Width);
    } else {
      setOffSetMultiple(-1);
    }

    return () => {
      setOffSetMultiple(0);
    };
  }, [dimensions, offSetMultiple, location.pathname, item1Width, item2Width, item3Width, item2Offset, item3Offset]);

  const handleMobileMenu = () => {
    setIsOpen((prevState) => !prevState);
  };

  const getRouteToNavigate = (privateRoutePath: PrivateRoutePath) => (active ? privateRoutePath : '#');

  const disableHeaderNavigationBar = dropdownVisible || searchDropdownVisible;

  return (
    <div
      className={`header-navigation ${active ? 'authenticated' : ''} ${isOpen ? 'expanded' : ''}`}
      onClick={() => handleMobileMenu()}
    >
      <div className="menu-items-list" ref={wrapperNavBarRef}>
        <div ref={item1Ref}>
          <NavLink
            id="Header_button___myTv"
            className={`context-switch ${active ? 'authenticated' : ''}`}
            activeClassName={`${!disableHeaderNavigationBar ? 'header-navigation-active' : ''}`}
            isActive={() => location.pathname.includes(PrivateRoutePath.MYTV)}
            to={getRouteToNavigate(PrivateRoutePath.MYTV)}
            onClick={() => {
              onMenuActiveHandler();
              setDropdownVisible(false);
            }}
          >
            <h4>{translate['menu.myTv']}</h4>
          </NavLink>
        </div>
        <div ref={item2Ref}>
          <NavLink
            id="Header_button___watchTv"
            className={`context-switch ${active ? 'authenticated' : ''}`}
            activeClassName={`${!disableHeaderNavigationBar ? 'header-navigation-active' : ''}`}
            isActive={() => location.pathname.includes(PrivateRoutePath.WATCHTV)}
            to={getRouteToNavigate(PrivateRoutePath.WATCHTV)}
            onClick={() => {
              onMenuActiveHandler();
              setDropdownVisible(false);
            }}
          >
            <h4>{translate['menu.watchTv']}</h4>
          </NavLink>
        </div>
        <div ref={item3Ref}>
          <NavLink
            id="Header_button___onDemand"
            className={`context-switch ${active ? 'authenticated' : ''}`}
            activeClassName={`${!disableHeaderNavigationBar ? 'header-navigation-active' : ''}`}
            isActive={() => location.pathname.includes(PrivateRoutePath.ONDEMAND)}
            to={getRouteToNavigate(PrivateRoutePath.ONDEMAND)}
            onClick={() => {
              onMenuActiveHandler();
              setDropdownVisible(false);
            }}
          >
            <h4>{translate['menu.onDemand']}</h4>
          </NavLink>
        </div>

        <div
          style={{
            transform: `translateX(${Math.round(offSetMultiple)}px)`,
            width: itemWith,
          }}
          className={`header-navigation-bar ${offSetMultiple >= 0 ? 'header-navigation-active' : ''}
        ${active ? 'authenticated' : ''}
        ${disableHeaderNavigationBar ? 'disabled' : ''}`}
        />
      </div>
      <div className={`dropdown-chevron context-switch ${active ? 'header-chevron' : ''}`} />
    </div>
  );
}
